import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { PLAN_ACCESS, surveyAccess } from "util/entitlements";
import { getSurveyCapabilities } from "util/surveyCapabilities";
import SurveyLockPage from "./SurveyLockPage";
import PlanEndedNotice from "components/General/PlanEndedNotice";
import useResizableColumns from "components/General/useResizableColumns";
import { getIntlLocale } from "util/locale";
import {
  Button,
  Input,
  Modal,
  Popconfirm,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
  message,
} from "antd";
import {
  AudioOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  SettingOutlined,
  StopOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import SurveyBuilderDrawer from "pages/SurveyManagement/SurveyBuilderDrawer";
import SurveyUpgradeModal from "pages/SurveyManagement/SurveyUpgradeModal";
import SurveyPreview from "pages/SurveyManagement/SurveyPreview";
import VoiceSurveyPreview from "pages/SurveyManagement/VoiceSurveyPreview";
import VoiceBrowserTest from "pages/SurveyManagement/VoiceBrowserTest";
import { loadTestValues } from "pages/SurveyManagement/voiceTestValues";
import { languageForUi } from "pages/Sonic/languageUtils";
import VoiceWidgetBuilderDrawer from "pages/VoiceCaptureManagement/VoiceWidgetBuilderDrawer";
import VoiceWidgetPreview from "pages/VoiceCaptureManagement/VoiceWidgetPreview";
import WidgetSetupDrawer from "pages/VoiceCaptureManagement/WidgetSetupDrawer";
import { CAPTURE_MODES } from "pages/VoiceCaptureManagement/constants";
import { checkFeedbackApiReady, getFeatureWaitlistStatus, joinFeatureWaitlist } from "pages/VoiceCaptureManagement/feedbackService";
import {
  createVoiceWidget,
  deleteVoiceWidget,
  listVoiceWidgets,
  saveVoiceWidgetChannels,
  setVoiceWidgetStatus,
  updateVoiceWidget,
} from "pages/VoiceCaptureManagement/voiceService";
import { SURVEY_RESPONSE_MODE, SURVEY_STATUS } from "pages/SurveyManagement/constants";
import {
  createSurvey,
  deleteSurvey,
  duplicateSurvey,
  listSurveys,
  videoAnswersEnabled,
  setSurveyStatus,
  updateSurvey,
} from "pages/SurveyManagement/surveyService";
import FormatLaunchpad from "./FormatLaunchpad";
import FormatSelectionModal from "./FormatSelectionModal";
import { NUMBER_SORT_DIRECTIONS, SURVEY_LIST_SORTERS, TEXT_SORT_DIRECTIONS } from "./sorters";
import {
  FEEDBACK_FORMAT,
  FORMAT_META,
  FORMAT_OPTIONS,
  STATUS_FILTERS,
  STATUS_META,
  VOICE_DELIVERY,
} from "./constants";
import surveyStyles from "../SurveyManagement/style.module.scss";
import styles from "./style.module.scss";

const FeedbackCapture = ({ user, theme: themeProp }) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const subscriptionStatus = useSelector((state) => state.userReducer);
  const access = surveyAccess(subscriptionStatus);
  // The plan has ended: surveys, answers and calls stay visible, nothing new is done.
  const readOnly = access === PLAN_ACCESS.READ_ONLY;
  const surveyCaps = useMemo(
    () => getSurveyCapabilities(subscriptionStatus),
    [subscriptionStatus],
  );
  const dashboardTheme = useSelector((state) => state.DashboardReducer.theme);
  const theme = themeProp || dashboardTheme || "light";
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [activeStatus, setActiveStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [apiReady, setApiReady] = useState(true);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeMode, setUpgradeMode] = useState("plus");

  const [formatModalOpen, setFormatModalOpen] = useState(false);
  const [formatModalOnVoice, setFormatModalOnVoice] = useState(false);
  const [newSurveyMode, setNewSurveyMode] = useState(SURVEY_RESPONSE_MODE.TEXT);
  const [selectedFormat, setSelectedFormat] = useState(null);

  const [surveyDrawerOpen, setSurveyDrawerOpen] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [editingStep, setEditingStep] = useState("build");
  const [voiceDrawerOpen, setVoiceDrawerOpen] = useState(false);
  const [editingVoice, setEditingVoice] = useState(null);
  const [voiceCreateVoiceOnly, setVoiceCreateVoiceOnly] = useState(false);
  const [setupDrawerOpen, setSetupDrawerOpen] = useState(false);
  const [setupWidget, setSetupWidget] = useState(null);

  const [saving, setSaving] = useState(false);
  const [rowBusyId, setRowBusyId] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);
  // A voice survey with a phone channel can be tried in the browser right from its preview.
  const previewPhoneCall = previewItem?.row?.survey?.distribution?.phone_call?.exists
    ? previewItem.row.survey.distribution.phone_call
    : null;
  const previewIsVoice = previewItem?.row?.survey?.responseMode === SURVEY_RESPONSE_MODE.VOICE;
  // Switching tabs re-renders the call preview, so it shows the values typed in the test tab.
  const [previewTab, setPreviewTab] = useState("call");
  const [waitlistJoined, setWaitlistJoined] = useState({});
  const [waitlistLoading, setWaitlistLoading] = useState({});

  const waitlistFeatures = useMemo(
    () =>
      FORMAT_OPTIONS.filter((option) => option.waitlistFeature).map(
        (option) => option.waitlistFeature
      ),
    []
  );

  const loadWaitlistStatus = useCallback(async () => {
    if (!waitlistFeatures.length) return;
    const results = await Promise.all(
      waitlistFeatures.map(async (feature) => {
        try {
          const status = await getFeatureWaitlistStatus(feature);
          return [feature, !!status.joined];
        } catch (_) {
          return [feature, false];
        }
      })
    );
    setWaitlistJoined(Object.fromEntries(results));
  }, [waitlistFeatures]);

  useEffect(() => {
    loadWaitlistStatus();
  }, [loadWaitlistStatus]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const apiStatus = await checkFeedbackApiReady();
      setApiReady(apiStatus.ready);
      const [surveys, widgets] = await Promise.all([
        listSurveys().catch(() => []),
        listVoiceWidgets().catch(() => []),
      ]);
      const unified = [
        ...surveys.map((s) => ({
          id: `survey-${s.id}`,
          rawId: s.id,
          format: FEEDBACK_FORMAT.TEXT,
          name: s.name,
          description: s.description,
          status: s.status,
          responseCount: s.responseCount || 0,
          lastResponseAt: s.lastResponseAt || null,
          // Phone (voice) surveys have no link people open.
          linkStats:
            s.responseMode === SURVEY_RESPONSE_MODE.VOICE
              ? null
              : { views: s.viewCount || 0, starts: s.startCount || 0 },
          createdAt: s.createdAt,
          metaLabel: `${s.questions?.length || 0} ${
            (s.questions?.length || 0) === 1
              ? t("surveyManagement.question_one")
              : t("surveyManagement.question_other")
          }`,
          // A voice survey is asked by the AI agent; today it goes out by phone.
          badgeFormat:
            s.responseMode === SURVEY_RESPONSE_MODE.VOICE ? FEEDBACK_FORMAT.VOICE : FEEDBACK_FORMAT.TEXT,
          badgeLabelKey:
            s.responseMode === SURVEY_RESPONSE_MODE.VOICE ? "feedbackCapture.format_badge_voice_call" : null,
          survey: s,
          widget: null,
        })),
        ...widgets.map((w) => ({
          id: `voice-${w.id}`,
          rawId: w.id,
          format: FEEDBACK_FORMAT.VOICE,
          name: w.name,
          description: w.description,
          status: w.status,
          responseCount: 0,
          createdAt: w.createdAt,
          metaLabel: t("feedbackCapture.voice_widget_meta"),
          badgeFormat: FEEDBACK_FORMAT.VOICE,
          badgeLabelKey: "feedbackCapture.format_badge_voice_widget",
          survey: null,
          widget: w,
        })),
      ].sort(
        (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
      );
      setRows(unified);
    } catch (e) {
      message.error(t("feedbackCapture.msg_load_error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const counts = useMemo(() => {
    const acc = { all: rows.length };
    rows.forEach((r) => {
      acc[r.status] = (acc[r.status] || 0) + 1;
    });
    return acc;
  }, [rows]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((r) => {
      const matchStatus = activeStatus === "all" || r.status === activeStatus;
      const matchSearch =
        !term ||
        r.name.toLowerCase().includes(term) ||
        (r.description || "").toLowerCase().includes(term);
      return matchStatus && matchSearch;
    });
  }, [rows, activeStatus, search]);

  const openFormatModal = () => {
    setFormatModalOnVoice(false);
    setFormatModalOpen(true);
  };

  // A voice pick without a delivery (the empty-state cards) asks call or widget first.
  const handleFormatSelect = (format, delivery) => {
    if (format === FEEDBACK_FORMAT.VOICE && !surveyCaps.canVoice) {
      setUpgradeMode("pro");
      setUpgradeOpen(true);
      return;
    }
    if (format === FEEDBACK_FORMAT.VOICE && !delivery) {
      setFormatModalOnVoice(true);
      setFormatModalOpen(true);
      return;
    }
    setFormatModalOpen(false);
    setSelectedFormat(format);
    if (format === FEEDBACK_FORMAT.TEXT || delivery === VOICE_DELIVERY.CALL) {
      setEditingSurvey(null);
      setNewSurveyMode(format === FEEDBACK_FORMAT.TEXT ? SURVEY_RESPONSE_MODE.TEXT : SURVEY_RESPONSE_MODE.VOICE);
      setSurveyDrawerOpen(true);
    } else if (format === FEEDBACK_FORMAT.VOICE) {
      setEditingVoice(null);
      setVoiceCreateVoiceOnly(true);
      setVoiceDrawerOpen(true);
    }
  };

  const openSurveyUpgrade = (tier = "plus") => {
    setUpgradeMode(tier === "pro_plus" ? "pro_plus" : tier === "pro" ? "pro" : "plus");
    setUpgradeOpen(true);
  };

  const handleJoinWaitlist = async (feature) => {
    if (!feature || waitlistJoined[feature]) return;
    setWaitlistLoading((prev) => ({ ...prev, [feature]: true }));
    try {
      const result = await joinFeatureWaitlist(feature);
      setWaitlistJoined((prev) => ({ ...prev, [feature]: true }));
      message.success(
        result.created
          ? t("feedbackCapture.waitlist_joined_success")
          : t("feedbackCapture.waitlist_already_joined")
      );
    } catch (e) {
      if (e?.message === "WAITLIST_UNAVAILABLE") {
        message.warning(t("feedbackCapture.waitlist_unavailable"));
      } else {
        message.error(t("feedbackCapture.waitlist_join_error"));
      }
    } finally {
      setWaitlistLoading((prev) => ({ ...prev, [feature]: false }));
    }
  };

  const openEdit = (row, step = "build") => {
    if (row.format === FEEDBACK_FORMAT.TEXT) {
      setEditingSurvey(row.survey);
      setEditingStep(step);
      setSurveyDrawerOpen(true);
    } else if (row.format === FEEDBACK_FORMAT.VOICE) {
      setEditingVoice(row.widget);
      setVoiceCreateVoiceOnly(false);
      setVoiceDrawerOpen(true);
    }
  };

  const openSetup = (row) => {
    setSetupWidget(row.widget);
    setSetupDrawerOpen(true);
  };

  // The responses page links back here with ?edit=<id> (and &step=distribute): open that survey's
  // editor once the list has loaded, then drop the query so a reload does not reopen it.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const editId = params.get("edit");
    if (!editId || loading) return;
    const matches = rows.filter((r) => String(r.rawId) === editId);
    const row = matches.find((r) => r.format === FEEDBACK_FORMAT.TEXT) || matches[0];
    history.replace(location.pathname);
    if (!row) return;
    const distribute = params.get("step") === "distribute";
    if (distribute && row.format === FEEDBACK_FORMAT.VOICE) openSetup(row);
    else openEdit(row, distribute ? "distribute" : "build");
  }, [location.search, loading, rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSurveySave = async (payload) => {
    setSaving(true);
    try {
      if (editingSurvey) {
        const updated = await updateSurvey(editingSurvey.id, payload);
        message.success(t("surveyManagement.msg_updated"));
        setRows((prev) =>
          prev.map((r) =>
            r.rawId === updated.id && r.format === FEEDBACK_FORMAT.TEXT
              ? {
                  ...r,
                  name: updated.name,
                  description: updated.description,
                  status: updated.status,
                  metaLabel: `${updated.questions?.length || 0} ${
                    (updated.questions?.length || 0) === 1
                      ? t("surveyManagement.question_one")
                      : t("surveyManagement.question_other")
                  }`,
                  survey: updated,
                }
              : r
          )
        );
      } else {
        const created = await createSurvey(payload);
        message.success(t("surveyManagement.msg_created"));
        setRows((prev) => [
          {
            id: `survey-${created.id}`,
            rawId: created.id,
            format: FEEDBACK_FORMAT.TEXT,
            name: created.name,
            description: created.description,
            status: created.status,
            responseCount: created.responseCount || 0,
            createdAt: created.createdAt,
            metaLabel: `${created.questions?.length || 0} ${
              (created.questions?.length || 0) === 1
                ? t("surveyManagement.question_one")
                : t("surveyManagement.question_other")
            }`,
            survey: created,
            widget: null,
          },
          ...prev,
        ]);
      }
      setSurveyDrawerOpen(false);
      setEditingSurvey(null);
      setSelectedFormat(null);
    } catch (e) {
      message.error(t("surveyManagement.msg_save_error"));
    } finally {
      setSaving(false);
    }
  };

  const handleVoiceSave = async (payload, channels) => {
    setSaving(true);
    try {
      if (editingVoice) {
        let updated = await updateVoiceWidget(editingVoice.id, payload);
        if (channels) {
          updated = await saveVoiceWidgetChannels(editingVoice.id, channels);
        }
        message.success(t("voiceCaptureManagement.msg_updated"));
        setRows((prev) =>
          prev.map((r) =>
            r.rawId === updated.id && r.format === FEEDBACK_FORMAT.VOICE
              ? {
                  ...r,
                  name: updated.name,
                  description: updated.description,
                  status: updated.status,
                  widget: updated,
                }
              : r
          )
        );
      } else {
        const created = await createVoiceWidget({
          ...payload,
          modes: voiceCreateVoiceOnly ? [CAPTURE_MODES.VOICE] : payload.modes,
        });
        message.success(t("voiceCaptureManagement.msg_created"));
        setRows((prev) => [
          {
            id: `voice-${created.id}`,
            rawId: created.id,
            format: FEEDBACK_FORMAT.VOICE,
            name: created.name,
            description: created.description,
            status: created.status,
            responseCount: 0,
            createdAt: created.createdAt,
            metaLabel: t("feedbackCapture.voice_widget_meta"),
            survey: null,
            widget: created,
          },
          ...prev,
        ]);
      }
      setVoiceDrawerOpen(false);
      setEditingVoice(null);
      setVoiceCreateVoiceOnly(false);
      setSelectedFormat(null);
    } catch (e) {
      message.error(t("voiceCaptureManagement.msg_save_error"));
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async (row) => {
    if (row.format !== FEEDBACK_FORMAT.TEXT) return;
    setRowBusyId(row.id);
    try {
      const copy = await duplicateSurvey(row.rawId);
      message.success(t("surveyManagement.msg_duplicated"));
      setRows((prev) => [
        {
          id: `survey-${copy.id}`,
          rawId: copy.id,
          format: FEEDBACK_FORMAT.TEXT,
          name: copy.name,
          description: copy.description,
          status: copy.status,
          responseCount: copy.responseCount || 0,
          createdAt: copy.createdAt,
          metaLabel: `${copy.questions?.length || 0} ${
            (copy.questions?.length || 0) === 1
              ? t("surveyManagement.question_one")
              : t("surveyManagement.question_other")
          }`,
          survey: copy,
          widget: null,
        },
        ...prev,
      ]);
    } catch (e) {
      message.error(t("surveyManagement.msg_dup_error"));
    } finally {
      setRowBusyId(null);
    }
  };

  const handleToggleStatus = async (row) => {
    const nextStatus =
      row.status === SURVEY_STATUS.ACTIVE
        ? SURVEY_STATUS.CLOSED
        : SURVEY_STATUS.ACTIVE;
    setRowBusyId(row.id);
    try {
      if (row.format === FEEDBACK_FORMAT.TEXT) {
        const updated = await setSurveyStatus(row.rawId, nextStatus);
        setRows((prev) =>
          prev.map((r) =>
            r.id === row.id ? { ...r, status: updated.status, survey: updated } : r
          )
        );
      } else {
        const updated = await setVoiceWidgetStatus(row.rawId, nextStatus);
        setRows((prev) =>
          prev.map((r) =>
            r.id === row.id ? { ...r, status: updated.status, widget: updated } : r
          )
        );
      }
      message.success(
        nextStatus === SURVEY_STATUS.ACTIVE
          ? t("surveyManagement.msg_activated")
          : t("surveyManagement.msg_closed")
      );
    } catch (e) {
      message.error(t("surveyManagement.msg_status_error"));
    } finally {
      setRowBusyId(null);
    }
  };

  const handleDelete = async (row) => {
    setRowBusyId(row.id);
    try {
      if (row.format === FEEDBACK_FORMAT.TEXT) {
        await deleteSurvey(row.rawId);
      } else {
        await deleteVoiceWidget(row.rawId);
      }
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      message.success(t("surveyManagement.msg_deleted"));
    } catch (e) {
      message.error(t("surveyManagement.msg_delete_error"));
    } finally {
      setRowBusyId(null);
    }
  };

  const renderFormatBadge = (row) => {
    const format = row.badgeFormat || row.format;
    const meta = FORMAT_META[format] || FORMAT_META[FEEDBACK_FORMAT.TEXT];
    const Icon = meta.icon;
    return (
      <Tag color={meta.color} className={styles.formatBadge}>
        <Icon className={styles.formatBadgeIcon} />
        {t(row.badgeLabelKey || meta.labelKey)}
      </Tag>
    );
  };

  const openResponses = (row) => {
    history.push(`/console/sonic-survey/${row.rawId}/responses`);
  };

  const baseColumns = [
    {
      title: t("feedbackCapture.col_format"),
      dataIndex: "format",
      key: "format",
      width: 130,
      sorter: SURVEY_LIST_SORTERS.format,
      sortDirections: TEXT_SORT_DIRECTIONS,
      render: (_, row) => renderFormatBadge(row),
    },
    {
      title: t("surveyManagement.col_survey"),
      dataIndex: "name",
      key: "name",
      sorter: SURVEY_LIST_SORTERS.name,
      sortDirections: TEXT_SORT_DIRECTIONS,
      render: (_, row) => (
        <div className={surveyStyles.nameCell}>
          <button
            type="button"
            className={surveyStyles.nameLink}
            onClick={() => openEdit(row)}
          >
            {row.name}
          </button>
          {row.description ? (
            <span className={surveyStyles.nameDesc} title={row.description}>
              {row.description}
            </span>
          ) : null}
          <span className={surveyStyles.questionMeta}>{row.metaLabel}</span>
        </div>
      ),
    },
    {
      title: t("surveyManagement.col_status"),
      dataIndex: "status",
      key: "status",
      width: 110,
      sorter: SURVEY_LIST_SORTERS.status,
      sortDirections: TEXT_SORT_DIRECTIONS,
      render: (status, row) => {
        const meta = STATUS_META[status] || STATUS_META.draft;
        const tag = <Tag color={meta.color}>{t(meta.labelKey)}</Tag>;
        return (row.survey || row.widget)?.closedByPlan ? (
          <Tooltip title={t("planEnded.closed_by_plan")}>{tag}</Tooltip>
        ) : (
          tag
        );
      },
    },
    {
      title: (
        <Tooltip title={t("feedbackCapture.views_tooltip")}>
          <span className={styles.viewsHeader}>{t("feedbackCapture.col_views")}</span>
        </Tooltip>
      ),
      dataIndex: "linkStats",
      key: "linkStats",
      width: 130,
      sorter: SURVEY_LIST_SORTERS.linkStats,
      sortDirections: NUMBER_SORT_DIRECTIONS,
      align: "right",
      render: (stats) => {
        if (!stats) return <span className={styles.viewsNone}>—</span>;
        const format = (n) => new Intl.NumberFormat(getIntlLocale(i18n.language)).format(n);
        return (
          <div className={styles.viewsCell}>
            <span className={styles.viewsCount}>{format(stats.views)}</span>
            <span className={styles.viewsStarted}>
              {t("feedbackCapture.views_started", { count: format(stats.starts) })}
            </span>
          </div>
        );
      },
    },
    {
      title: t("surveyManagement.col_responses"),
      dataIndex: "responseCount",
      key: "responseCount",
      width: 120,
      sorter: SURVEY_LIST_SORTERS.responseCount,
      sortDirections: NUMBER_SORT_DIRECTIONS,
      align: "right",
      render: (count, row) => {
        const value = count || 0;
        return (
          <button
            type="button"
            className={styles.responseCountBtn}
            onClick={() => openResponses(row)}
            title={t("feedbackCapture.view_responses")}
          >
            {new Intl.NumberFormat(getIntlLocale(i18n.language)).format(value)}
          </button>
        );
      },
    },
    {
      title: t("surveyManagement.col_last_response"),
      dataIndex: "lastResponseAt",
      key: "lastResponseAt",
      width: 160,
      sorter: SURVEY_LIST_SORTERS.lastResponseAt,
      sortDirections: NUMBER_SORT_DIRECTIONS,
      render: (lastResponseAt) =>
        lastResponseAt ? (
          <span title={dayjs(lastResponseAt).format("D MMM YYYY HH:mm")}>
            {dayjs(lastResponseAt).format("D MMM YYYY, HH:mm")}
          </span>
        ) : (
          "—"
        ),
    },
    {
      title: t("surveyManagement.col_created"),
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      sorter: SURVEY_LIST_SORTERS.createdAt,
      sortDirections: NUMBER_SORT_DIRECTIONS,
      defaultSortOrder: "descend",
      render: (createdAt) =>
        createdAt ? (
          <span title={dayjs(createdAt).format("D MMM YYYY HH:mm")}>
            {dayjs(createdAt).format("D MMM YYYY")}
          </span>
        ) : (
          "—"
        ),
    },
    {
      title: t("surveyManagement.col_actions"),
      key: "actions",
      width: 190,
      render: (_, row) => {
        const isActive = row.status === SURVEY_STATUS.ACTIVE;
        const busy = rowBusyId === row.id;
        const previewDisplayMode =
          row.format === FEEDBACK_FORMAT.TEXT
            ? row.survey?.distribution?.web_embed?.enabled &&
              row.survey.distribution.web_embed.displayMode
              ? row.survey.distribution.web_embed.displayMode
              : "popup"
            : row.widget?.channels?.find((c) => c.channel_type === "web_embed")
                ?.config?.display_mode || "popup";

        const preview = (
          <Tooltip title={t("surveyManagement.action_preview")}>
            <Button
              size="small"
              type="text"
              icon={<EyeOutlined />}
              onClick={() => setPreviewItem({ row, previewDisplayMode })}
            />
          </Tooltip>
        );
        if (readOnly) {
          return <div className={surveyStyles.rowActions}>{preview}</div>;
        }

        return (
          <div className={surveyStyles.rowActions}>
            {preview}
            <Tooltip title={t("surveyManagement.action_edit")}>
              <Button
                size="small"
                type="text"
                icon={<EditOutlined />}
                onClick={() => openEdit(row)}
              />
            </Tooltip>
            {row.format === FEEDBACK_FORMAT.VOICE ? (
              <Tooltip title={t("feedbackCapture.action_setup")}>
                <Button
                  size="small"
                  type="text"
                  icon={<SettingOutlined />}
                  onClick={() => openSetup(row)}
                />
              </Tooltip>
            ) : (
              <Tooltip title={t("surveyManagement.action_duplicate")}>
                <Button
                  size="small"
                  type="text"
                  icon={<CopyOutlined />}
                  loading={busy}
                  onClick={() => handleDuplicate(row)}
                />
              </Tooltip>
            )}
            <Tooltip
              title={
                isActive
                  ? t("surveyManagement.action_close")
                  : t("surveyManagement.action_activate")
              }
            >
              <Button
                size="small"
                type="text"
                icon={isActive ? <StopOutlined /> : <PlayCircleOutlined />}
                loading={busy}
                onClick={() => handleToggleStatus(row)}
              />
            </Tooltip>
            <Popconfirm
              title={t("surveyManagement.delete_confirm_title")}
              description={t("surveyManagement.delete_confirm_desc")}
              okText={t("surveyManagement.delete")}
              cancelText={t("surveyManagement.cancel")}
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDelete(row)}
            >
              <Tooltip title={t("surveyManagement.action_delete")}>
                <Button
                  size="small"
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  const {
    columns,
    tableProps: resizableTableProps,
    tableClassName: resizableTableClassName,
  } = useResizableColumns(baseColumns, {
    storageKey: "pv_survey_list_column_widths",
    flexKey: "name",
    resizeLabel: t("feedbackCapture.resize_column"),
  });

  const tabItems = STATUS_FILTERS.map((f) => ({
    key: f.key,
    label: `${t(f.labelKey)} (${counts[f.key] || 0})`,
  }));

  const hasInstruments = rows.length > 0;
  const showLaunchpad = !loading && !hasInstruments;

  if (access === PLAN_ACCESS.NONE) {
    return (
      <SurveyLockPage
        theme={theme}
        userId={user?.uid}
        onActivated={() => window.location.reload()}
      />
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderText}>
          <h1 className={surveyStyles.pageTitle}>
            {t("feedbackCapture.unified_title")}
          </h1>
          <p className={surveyStyles.pageSubtitle}>
            {t("feedbackCapture.unified_subtitle")}
          </p>
        </div>
        <div className={styles.pageHeaderActions}>
          {hasInstruments ? (
            <Button icon={<SettingOutlined />} onClick={() => history.push("/console/sonic-survey/settings")}>
              {t("surveySettings.open")}
            </Button>
          ) : null}
          {hasInstruments && !readOnly ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={openFormatModal}>
              {t("feedbackCapture.create_instrument")}
            </Button>
          ) : null}
        </div>
      </div>

      {readOnly ? <PlanEndedNotice product="survey" /> : null}

      {loading && !hasInstruments ? (
        <div className={styles.launchpadLoading}>
          <Spin />
        </div>
      ) : null}

      {showLaunchpad && !readOnly ? (
        <FormatLaunchpad
          onSelect={handleFormatSelect}
          waitlistJoined={waitlistJoined}
          waitlistLoading={waitlistLoading}
          onJoinWaitlist={handleJoinWaitlist}
          capabilities={surveyCaps}
          onUpgrade={openSurveyUpgrade}
        />
      ) : hasInstruments ? (
        <div className={surveyStyles.contentPanel}>
          <div className={surveyStyles.toolbar}>
            <Tabs
              activeKey={activeStatus}
              onChange={setActiveStatus}
              items={tabItems}
              className={surveyStyles.statusTabs}
            />
            <div className={surveyStyles.toolbarRight}>
              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder={t("surveyManagement.search_placeholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ width: 220 }}
              />
              <Tooltip title={t("surveyManagement.refresh")}>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={fetchAll}
                  loading={loading}
                />
              </Tooltip>
            </div>
          </div>

          <Spin spinning={loading}>
            <Table
              rowKey="id"
              columns={columns}
              dataSource={filteredRows}
              pagination={{ pageSize: 10, hideOnSinglePage: true }}
              locale={{
                emptyText: (
                  <div className={surveyStyles.tableEmpty}>
                    {t("surveyManagement.empty_no_match")}
                  </div>
                ),
              }}
              className={`${surveyStyles.surveyTable} ${resizableTableClassName}`}
              {...resizableTableProps}
            />
          </Spin>
        </div>
      ) : null}

      <FormatSelectionModal
        open={formatModalOpen}
        onClose={() => setFormatModalOpen(false)}
        onSelect={handleFormatSelect}
        startOnVoice={formatModalOnVoice}
        waitlistJoined={waitlistJoined}
        waitlistLoading={waitlistLoading}
        onJoinWaitlist={handleJoinWaitlist}
        capabilities={surveyCaps}
        onUpgrade={openSurveyUpgrade}
      />

      <SurveyBuilderDrawer
        open={surveyDrawerOpen}
        survey={editingSurvey}
        saving={saving}
        formatType={FEEDBACK_FORMAT.TEXT}
        responseMode={newSurveyMode}
        initialStep={editingStep}
        onClose={() => {
          setSurveyDrawerOpen(false);
          setEditingSurvey(null);
          setEditingStep("build");
          setSelectedFormat(null);
        }}
        onSave={handleSurveySave}
        onUpgrade={openSurveyUpgrade}
        videoEnabled={videoAnswersEnabled()}
      />

      <SurveyUpgradeModal
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        mode={upgradeMode}
        returnPath="/console/sonic-survey"
        user={user}
        onSuccess={fetchAll}
      />

      <VoiceWidgetBuilderDrawer
        open={voiceDrawerOpen}
        widget={editingVoice}
        saving={saving}
        apiReady={apiReady}
        voiceOnly={voiceCreateVoiceOnly}
        onClose={() => {
          setVoiceDrawerOpen(false);
          setEditingVoice(null);
          setVoiceCreateVoiceOnly(false);
          setSelectedFormat(null);
        }}
        onSave={handleVoiceSave}
      />

      <WidgetSetupDrawer
        open={setupDrawerOpen}
        widget={setupWidget}
        apiReady={apiReady}
        onClose={() => {
          setSetupDrawerOpen(false);
          setSetupWidget(null);
        }}
        onWidgetUpdated={(updated) => {
          setRows((prev) =>
            prev.map((r) =>
              r.rawId === updated.id && r.format === FEEDBACK_FORMAT.VOICE
                ? { ...r, widget: updated }
                : r
            )
          );
          setSetupWidget(updated);
        }}
      />


      <Modal
        title={
          previewIsVoice ? (
            <span className={styles.voicePreviewModalTitle}>
              <span className={styles.voicePreviewModalIcon}>
                <AudioOutlined />
              </span>
              {previewItem.row.name || t("surveyManagement.preview_title")}
            </span>
          ) : (
            previewItem?.row?.name || t("surveyManagement.preview_title")
          )
        }
        open={!!previewItem}
        onCancel={() => setPreviewItem(null)}
        afterClose={() => setPreviewTab("call")}
        footer={[
          <Button key="close" onClick={() => setPreviewItem(null)}>
            {t("surveyManagement.preview_close")}
          </Button>,
        ]}
        width={previewIsVoice ? 720 : 520}
        className={previewIsVoice ? styles.voicePreviewModal : undefined}
        destroyOnClose
      >
        {previewIsVoice ? (
          <div className={surveyStyles.previewModalBody}>
            {previewPhoneCall ? (
              // An inactive tab stays mounted, so a running test goes on while the questions are read.
              <Tabs
                activeKey={previewTab}
                onChange={setPreviewTab}
                items={[
                  {
                    key: "call",
                    label: t("surveyManagement.voice_preview_title"),
                    children: (
                      <VoiceSurveyPreview
                        questions={previewItem.row.survey.questions || []}
                        greeting={previewPhoneCall.greeting || previewItem.row.survey.callGreeting}
                        thankYouMessage={previewItem.row.survey.thankYouMessage}
                        values={{
                          ...loadTestValues(),
                          ...(previewPhoneCall.brandName ? { brand: previewPhoneCall.brandName } : {}),
                        }}
                        inTabs
                      />
                    ),
                  },
                  {
                    key: "test",
                    label: t("surveyManagement.voice_preview_test_tab"),
                    children: (
                      <VoiceBrowserTest
                        instrumentId={previewItem.row.survey.id}
                        surveyTitle={previewItem.row.survey.name}
                        questions={previewItem.row.survey.questions || []}
                        phoneCall={previewPhoneCall}
                        language={
                          previewPhoneCall.language ||
                          languageForUi(previewItem.row.survey.primaryLanguage || i18n.language)
                        }
                        hint={t("surveyManagement.distribute.phone.test_hint_saved")}
                      />
                    ),
                  },
                ]}
              />
            ) : (
              <VoiceSurveyPreview
                questions={previewItem.row.survey.questions || []}
                greeting={previewItem.row.survey.distribution?.phone_call?.greeting || previewItem.row.survey.callGreeting}
                thankYouMessage={previewItem.row.survey.thankYouMessage}
              />
            )}
          </div>
        ) : previewItem?.row?.format === FEEDBACK_FORMAT.TEXT ? (
          <div className={surveyStyles.previewModalBody}>
            <SurveyPreview
              name={previewItem.row.survey?.name}
              description={previewItem.row.survey?.description}
              questions={previewItem.row.survey?.questions || []}
              displayMode={previewItem.previewDisplayMode}
              widgetHeaderTitle={previewItem.row.survey?.widgetHeaderTitle || ""}
              showBranding={previewItem.row.survey?.showBranding !== false}
              requestContactDetails={previewItem.row.survey?.requestContactDetails === true}
              contactDetailsRequired={previewItem.row.survey?.contactDetailsRequired === true}
              primaryColor={previewItem.row.survey?.primaryColor}
              logoUrl={previewItem.row.survey?.logoUrl}
              popupPosition={previewItem.row.survey?.popupPosition}
              formatType={FEEDBACK_FORMAT.TEXT}
            />
          </div>
        ) : previewItem?.row?.format === FEEDBACK_FORMAT.VOICE ? (
          <VoiceWidgetPreview
            prompt={previewItem.row.widget?.prompt}
            modes={
              previewItem.row.widget?.modes || [CAPTURE_MODES.VOICE]
            }
            displayMode={previewItem.previewDisplayMode}
          />
        ) : null}
      </Modal>
    </div>
  );
};

export default FeedbackCapture;
